import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import moment from "moment";

import { useLargeReportConfirm } from "src/hooks/useLargeReportConfirm";
import { useReportRangeLimit } from "src/hooks/useReportRangeLimit";

import { ActionButton, CardLayout, SelectField } from "../v3";
import { HumanResourceService, ProjectService } from "../../services";
import { workerReportValidation } from "../../pages/report/validation";
import { getFilteredOptions, selectAllOption } from "../../utils";
import { ReportDateRangeFields } from "./ReportDateRangeFields.component";

export const WorkerReportForm = ({ reportType = "summary", handleClear = () => {}, onGenerate = () => {} }) => {
  const { months: maxRangeMonths } = useReportRangeLimit();
  const { confirmLargeRange, largeRangeDialog } = useLargeReportConfirm();
  const [selectedWorkers, setSelectedWorkers] = useState([selectAllOption]);
  const [selectedProjects, setSelectedProjects] = useState([selectAllOption]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [reportDetails, setReportDetails] = useState({
    workerIds: null,
    projectIds: null,
    accountIds: null,
    overtime: "all",
    paid: "all",
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  const [searchParams] = useSearchParams();
  const workerId = searchParams.get("workerId");

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  useEffect(() => {
    if (!workerId) return;

    const fetchWorker = async () => {
      setLoading(true);
      try {
        const { data: worker } = await HumanResourceService.getEmployeeById(workerId);
        setSelectedWorkers([worker]);
        setReportDetails((prev) => ({
          ...prev,
          workerIds: [worker.id],
          accountIds: worker.accountId ? [worker.accountId] : null,
        }));
        setErrors((prev) => ({ ...prev, workerIds: "" }));
      } catch (error) {
        toast.error("Failed to fetch worker details");
        console.error("🚀 ~ fetchWorker ~ error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorker();
  }, [workerId]);

  const updateDetailsState = (event) => {
    setLoading(false);
    handleClear();
    const { name, value } = event.target;
    setReportDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "fromDate") setErrors((prev) => ({ ...prev, fromDate: "" }));
    else if (name === "toDate") setErrors((prev) => ({ ...prev, toDate: "" }));
  };

  const handleProjectChange = (projects) => {
    setLoading(false);
    handleClear();
    const allSelected = projects.some((project) => project.id === "all");

    if (allSelected) {
      setSelectedProjects([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, projectIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = projects.filter((project) => project.id !== "all");
      setSelectedProjects(filtered);
      setReportDetails((prev) => ({ ...prev, projectIds: filtered.map((project) => project.id) }));
    }

    setErrors((prev) => ({ ...prev, projectIds: "" }));
  };

  const handleWorkerChange = (workers) => {
    setLoading(false);
    handleClear();
    const allSelected = workers.some((worker) => worker.id === "all");

    if (allSelected) {
      setSelectedWorkers([selectAllOption]);
      setReportDetails((prev) => ({ ...prev, workerIds: null }));
    } else {
      // remove "all" if previously selected
      const filtered = workers.filter((worker) => worker.id !== "all");
      setSelectedWorkers(filtered);
      setReportDetails((prev) => ({
        ...prev,
        workerIds: filtered.map((worker) => worker.id),
        accountIds: filtered.map((worker) => worker.accountId),
      }));
    }

    setErrors((prev) => ({ ...prev, workerIds: "" }));
  };

  const handleOvertimeChange = (option) => {
    setLoading(false);
    handleClear();
    setReportDetails((prev) => ({
      ...prev,
      overtime: option?.id || "all",
    }));
    setErrors((prev) => ({ ...prev, overtime: "" }));
  };

  const handlePaidChange = (option) => {
    setLoading(false);
    handleClear();
    setReportDetails((prev) => ({
      ...prev,
      paid: option?.id || "all",
    }));
    setErrors((prev) => ({ ...prev, paid: "" }));
  };

  const handleGenerateReport = async () => {
    setErrors({});
    const validationErrors = workerReportValidation(
      reportDetails,
      selectedProjects[0]?.id,
      selectedWorkers[0]?.id,
      maxRangeMonths,
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!(await confirmLargeRange(reportDetails.fromDate, reportDetails.toDate))) return;

    try {
      setLoading(true);

      const isoFromDate = moment(reportDetails.fromDate).startOf("day").toISOString();
      const isoToDate = moment(reportDetails.toDate).endOf("day").toISOString();

      const payload = {
        ...reportDetails,
        fromDate: isoFromDate,
        toDate: isoToDate,
        reportType,
      };

      onGenerate?.(payload, {
        onError: () => setLoading(false),
      });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  const overtimeOptions = [
    { id: "all", name: "All" },
    { id: "true", name: "Overtime" },
    { id: "false", name: "No Overtime" },
  ];

  const paidOptions = [
    { id: "all", name: "All" },
    { id: "true", name: "Paid" },
    { id: "false", name: "Unpaid" },
  ];

  return (
    <>
      <CardLayout>
        <Box mt={1}>
          <Box display="flex" gap={2}>
            <SelectField
              label="Worker"
              value={selectedWorkers}
              getData={HumanResourceService.getEmployees}
              getDataStaticFilters={{ type: "WORKER", sortBy: "name", sortOrder: "asc" }}
              disabled={!!workerId && selectedWorkers?.some((worker) => worker.id === workerId)}
              required
              multiSelect
              error={!!errors.workerIds}
              helperText={errors.workerIds}
              onChange={handleWorkerChange}
              optionFilter={(options) => getFilteredOptions(options, selectedWorkers, selectAllOption)}
              style={{ width: "100%" }}
            />
            <SelectField
              label="Project"
              value={selectedProjects}
              getData={ProjectService.getProjectList}
              getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
              required
              multiSelect
              error={!!errors.projectIds}
              helperText={errors.projectIds}
              onChange={handleProjectChange}
              optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
              style={{ width: "100%" }}
            />
          </Box>
          {/* <Box display="flex" gap={2}>
            <SelectField
              label="Overtime"
              value={overtimeOptions.find((opt) => opt.id === reportDetails.overtime)}
              getData={() => Promise.resolve({ data: overtimeOptions })}
              onChange={handleOvertimeChange}
              style={{ width: "100%" }}
            />

            <SelectField
              label="Paid"
              value={paidOptions.find((opt) => opt.id === reportDetails.paid)}
              getData={() => Promise.resolve({ data: paidOptions })}
              onChange={handlePaidChange}
              style={{ width: "100%" }}
            />
          </Box> */}
          <ReportDateRangeFields
            fromDate={reportDetails.fromDate}
            toDate={reportDetails.toDate}
            onChange={updateDetailsState}
            errors={errors}
          />
          {largeRangeDialog}
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
        </Box>
      </CardLayout>
    </>
  );
};
